/**
 * Parse a Response object into a typed MessageApiError
 */
import type { MessageApiError } from "./error-types";

export function parseResponseError(response: Response): MessageApiError {
  const statusCode = response.status;

  if (statusCode === 401) {
    return {
      type: "AuthError",
      code: "AUTH_EXPIRED",
      message: "Authentication token expired",
      userMessage: "Your session has expired. Please log in again.",
      statusCode,
      shouldRetry: false,
      requiresReauth: true,
    };
  }

  if (statusCode === 403) {
    return {
      type: "AuthError",
      code: "FORBIDDEN",
      message: "Access denied",
      userMessage: "You don't have permission to perform this action.",
      statusCode,
      shouldRetry: false,
      requiresReauth: false,
    };
  }

  if (statusCode === 404) {
    return {
      type: "NotFoundError",
      code: "NOT_FOUND",
      message: "Resource not found",
      userMessage: "The requested item could not be found.",
      statusCode,
      shouldRetry: false,
    };
  }

  if (statusCode === 409) {
    return {
      type: "ConflictError",
      code: "CONFLICT",
      message: "Resource conflict",
      userMessage: "The resource has been modified. Please refresh and try again.",
      statusCode,
      shouldRetry: true,
      retryAfterMs: 1000,
    };
  }

  if (statusCode === 429) {
    const retryAfter = response.headers.get("Retry-After");
    const retryAfterMs = retryAfter ? parseInt(retryAfter) * 1000 : 5000;

    return {
      type: "RateLimitError",
      code: "RATE_LIMITED",
      message: "Rate limit exceeded",
      userMessage: "Too many requests. Please wait a moment and try again.",
      statusCode,
      shouldRetry: true,
      retryAfterMs,
    };
  }

  if (statusCode >= 400 && statusCode < 500) {
    return {
      type: "ValidationError",
      code: "VALIDATION_ERROR",
      message: `Client error: ${statusCode}`,
      userMessage: "Invalid request. Please check your input and try again.",
      statusCode,
      shouldRetry: false,
    };
  }

  if (statusCode === 500) {
    return {
      type: "ServerError",
      code: "SERVER_ERROR",
      message: "Internal server error",
      userMessage: "Server error. Please try again later.",
      statusCode,
      shouldRetry: true,
      retryAfterMs: 3000,
    };
  }

  if (statusCode === 503) {
    return {
      type: "ServerError",
      code: "SERVICE_UNAVAILABLE",
      message: "Service unavailable",
      userMessage: "Service is temporarily unavailable. Please try again later.",
      statusCode,
      shouldRetry: true,
      retryAfterMs: 5000,
    };
  }

  if (statusCode >= 500) {
    return {
      type: "ServerError",
      code: "SERVER_ERROR",
      message: `Server error: ${statusCode}`,
      userMessage: "Server error. Please try again later.",
      statusCode,
      shouldRetry: true,
      retryAfterMs: 3000,
    };
  }

  return {
    type: "UnknownError",
    code: "UNKNOWN_ERROR",
    message: `HTTP ${statusCode}`,
    userMessage: "An unexpected error occurred. Please try again.",
    statusCode,
    shouldRetry: false,
  };
}
